import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, MessageSquare, RotateCcw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import CarouselViewer from "@/components/CarouselViewer";
import FeedGrid from "@/components/FeedGrid";
import { SocialNetworksPanel } from "@/components/graphisme/SocialNetworks";
import CalendarMonth from "@/components/CalendarMonth";
import { formatLabel, frDate, isVideo, platformLabel } from "@/lib/constants";

const CM_STATUS = {
  a_valider: { label: "À valider", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  valide: { label: "Validée", cls: "bg-green-50 text-green-700 border-green-200" },
  modification: { label: "Modification demandée", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  planifie: { label: "Planifiée", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  programme: { label: "Programmée", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  publication_en_cours: { label: "Publication en cours", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  publie: { label: "Publiée", cls: "bg-green-50 text-green-700 border-green-200" },
  erreur_publication: { label: "Validée", cls: "bg-green-50 text-green-700 border-green-200" },
};
const VALIDATED = ["valide", "planifie", "programme", "publie", "publication_en_cours", "erreur_publication"];

import CMClientReports from "@/components/graphisme/CMClientReports";

const SUBTABS = [
  { id: "a_valider", label: "À valider", count: true },
  { id: "calendrier", label: "Calendrier" },
  { id: "feed", label: "Aperçu du feed" },
  { id: "validees", label: "Publications validées", count: true },
  { id: "reseaux", label: "Réseaux sociaux" },
  { id: "rapports", label: "Rapports" },
];

function PubVisual({ p, color }) {
  if (p.format === "carrousel" && (p.slides || []).length > 0) {
    return <CarouselViewer slides={p.slides} title={p.title} testid={`cm-carousel-${p.id}`} />;
  }
  if (p.visual_url && isVideo(p.visual_url)) {
    return <video src={p.visual_url} controls playsInline className="w-full aspect-[4/5] object-contain bg-black" data-testid={`cm-video-${p.id}`} />;
  }
  if (p.visual_url) {
    return <img src={p.visual_url} alt={p.title} className="w-full" data-testid={`cm-img-${p.id}`} />;
  }
  return (
    <div className="aspect-[4/5] flex items-center justify-center text-white font-heading font-bold text-xl p-6 text-center" style={{ background: color }}>
      {p.title}
    </div>
  );
}

function PubCard({ p, color, actionable, onValidate, onChangesSubmit, changesFor, setChangesFor,
                   changeText, setChangeText, commentFor, setCommentFor, commentText, setCommentText, onComment }) {
  const st = CM_STATUS[p.status] || { label: p.status, cls: "bg-neutral-100 text-neutral-500 border-neutral-200" };
  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden" data-testid={`cm-pub-${p.id}`}>
      <div className="flex items-center gap-3 px-4 sm:px-5 py-4">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{p.title}</p>
          <p className="text-xs text-neutral-400">
            {frDate(p.date)}{p.time ? ` · ${p.time}` : ""} · {platformLabel(p.platform)} {formatLabel(p.format)}
          </p>
          {p.status === "programme" && p.scheduled_at && (
            <p className="text-xs text-blue-600 font-medium mt-0.5" data-testid={`cm-scheduled-${p.id}`}>
              Programmée pour le {new Date(p.scheduled_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", timeZone: p.timezone || "Europe/Paris" })} à {new Date(p.scheduled_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: p.timezone || "Europe/Paris" })}
            </p>
          )}
          {p.status === "publie" && Object.entries(p.channels_status || {}).some(([, s]) => s.status === "published" && s.permalink) && (
            <p className="text-xs mt-0.5 flex gap-3" data-testid={`cm-published-links-${p.id}`}>
              {Object.entries(p.channels_status || {}).filter(([, s]) => s.status === "published" && s.permalink).map(([ch, s]) => (
                <a key={ch} href={s.permalink} target="_blank" rel="noreferrer" className="text-green-700 hover:underline font-medium">
                  Voir sur {ch === "instagram" ? "Instagram" : "Facebook"} ↗
                </a>
              ))}
            </p>
          )}
        </div>
        <span className={`text-[11px] rounded-full px-2.5 py-1 border shrink-0 ${st.cls}`} data-testid={`cm-status-${p.id}`}>{st.label}</span>
      </div>

      <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-neutral-100 pt-4">
        <div className="relative rounded-xl overflow-hidden bg-neutral-100 max-w-sm mx-auto w-full" data-testid={`cm-visual-${p.id}`}>
          <PubVisual p={p} color={color} />
        </div>

        {p.description && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Description</p>
            <p className="text-sm text-neutral-700 whitespace-pre-line">{p.description}</p>
          </div>
        )}
        {p.hashtags && <p className="text-sm text-blue-600 break-words">{p.hashtags}</p>}
        {p.cta && <p className="text-sm font-medium">{p.cta}</p>}

        {(p.comments || []).length > 0 && (
          <div className="space-y-2 pt-1">
            {p.comments.map((c) => (
              <div key={c.id} className="flex gap-2.5" data-testid={`cm-comment-${c.id}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${c.author_type === "client" ? "text-white" : "bg-neutral-900 text-white"}`}
                  style={c.author_type === "client" ? { background: color } : {}}>
                  {c.author_name?.[0]?.toUpperCase()}
                </div>
                <div className="bg-neutral-50 border border-neutral-100 rounded-lg px-3 py-2 flex-1 min-w-0">
                  <p className="text-xs font-medium">{c.author_name}</p>
                  <p className="text-sm text-neutral-600 break-words">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {actionable && (changesFor === p.id ? (
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-4 space-y-3" data-testid={`cm-changes-form-${p.id}`}>
            <p className="text-sm font-medium text-red-700">Qu'est-ce qui doit être modifié ?</p>
            <Textarea rows={3} autoFocus value={changeText} onChange={(e) => setChangeText(e.target.value)}
              placeholder="Ex : Peut-on remplacer cette photo ?" data-testid={`cm-changes-input-${p.id}`} />
            <div className="flex gap-2">
              <Button onClick={() => onChangesSubmit(p)} className="bg-red-600 hover:bg-red-700 text-white gap-1.5 flex-1" data-testid={`cm-changes-submit-${p.id}`}>
                <Send className="w-4 h-4" /> Envoyer la demande
              </Button>
              <Button variant="outline" onClick={() => setChangesFor(null)} data-testid={`cm-changes-cancel-${p.id}`}>Annuler</Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-1.5" data-testid={`cm-validate-${p.id}`}
              onClick={() => onValidate(p)}>
              <CheckCircle2 className="w-4 h-4" /> Valider
            </Button>
            <Button variant="outline" className="flex-1 gap-1.5" data-testid={`cm-changes-${p.id}`}
              onClick={() => { setChangesFor(p.id); setChangeText(""); }}>
              <RotateCcw className="w-4 h-4" /> Demander une modification
            </Button>
          </div>
        ))}

        {commentFor === p.id ? (
          <div className="flex gap-2">
            <input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Votre commentaire…"
              className="flex-1 border border-neutral-200 rounded-lg px-3 py-1.5 text-sm" data-testid={`cm-comment-input-${p.id}`} />
            <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid={`cm-comment-send-${p.id}`}
              onClick={() => onComment(p)}>Envoyer</Button>
          </div>
        ) : (
          <button onClick={() => { setCommentFor(p.id); setCommentText(""); }}
            className="text-xs text-neutral-400 hover:text-neutral-700 flex items-center gap-1.5 transition-colors" data-testid={`cm-comment-open-${p.id}`}>
            <MessageSquare className="w-3.5 h-3.5" /> Commenter
          </button>
        )}
      </div>
    </div>
  );
}

export function CMPortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = ["a_valider", "calendrier", "feed", "validees", "reseaux", "rapports"].includes(searchParams.get("cm"))
    ? searchParams.get("cm") : "a_valider";
  const [pubs, setPubs] = useState(null);
  const [color, setColor] = useState("#395963");
  const [month, setMonth] = useState(new Date());
  const [changesFor, setChangesFor] = useState(null);
  const [changeText, setChangeText] = useState("");
  const [commentFor, setCommentFor] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [headerH, setHeaderH] = useState(105);

  // Le sous-menu reste collé sous l'en-tête du portail : seules les publications défilent
  useEffect(() => {
    const measure = () => setHeaderH(document.querySelector("header")?.offsetHeight || 105);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const load = () => api.get("/graphisme/espace/cm/publications").then((r) => setPubs(r.data)).catch(() => setPubs([]));
  useEffect(() => {
    load();
    api.get("/graphisme/auth/me").then((r) => setColor(r.data?.color || "#395963")).catch(() => {});
  }, []);

  const setSub = (id) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    p.set("tab", "cm");
    p.set("cm", id);
    return p;
  }, { replace: true });

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); load(); }
  };

  const validate = (p) => {
    setPubs((ps) => ps.map((x) => (x.id === p.id ? { ...x, status: "valide", validation_status: "valide" } : x)));
    act(() => api.post(`/graphisme/espace/cm/publications/${p.id}/validate`),
      "Publication validée — elle passe dans « Publications validées »");
  };

  const submitChanges = (p) => {
    if (!changeText.trim()) { toast.error("Décrivez la modification souhaitée"); return; }
    setPubs((ps) => ps.map((x) => (x.id === p.id ? { ...x, status: "modification", validation_status: "modification" } : x)));
    setChangesFor(null);
    act(() => api.post(`/graphisme/espace/cm/publications/${p.id}/request-changes`, { text: changeText.trim() }),
      "Modification demandée — votre community manager est notifiée");
  };

  const sendComment = (p) => {
    if (!commentText.trim()) return;
    const text = commentText.trim();
    setCommentFor(null);
    setCommentText("");
    act(() => api.post(`/graphisme/espace/cm/publications/${p.id}/comment`, { text }), "Commentaire envoyé");
  };

  if (pubs === null) {
    return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  }

  const aValider = pubs.filter((p) => p.status === "a_valider");
  const validees = pubs.filter((p) => VALIDATED.includes(p.status));
  const counts = { a_valider: aValider.length, validees: validees.length };
  const cardProps = { color, changesFor, setChangesFor, changeText, setChangeText,
                      commentFor, setCommentFor, commentText, setCommentText,
                      onValidate: validate, onChangesSubmit: submitChanges, onComment: sendComment };

  return (
    <div className="space-y-5" data-testid="cm-portal">
      <div className="sticky z-10 -mx-4 px-4 pt-1 bg-neutral-100 flex gap-5 border-b border-neutral-200 overflow-x-auto"
        style={{ top: headerH }} data-testid="cm-subnav">
        {SUBTABS.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)} data-testid={`cm-sub-${t.id}`}
            className={`pb-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
              sub === t.id ? "border-neutral-900 text-neutral-900 font-semibold" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>
            {t.label}{t.count ? ` (${counts[t.id]})` : ""}
          </button>
        ))}
      </div>

      {sub === "a_valider" && (
        <div className="space-y-4" data-testid="cm-list-a-valider">
          {aValider.length === 0 && (
            <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">
              Aucune publication en attente de validation — tout est à jour.
            </p>
          )}
          {aValider.map((p) => <PubCard key={p.id} p={p} actionable {...cardProps} />)}
        </div>
      )}

      {sub === "calendrier" && (
        <div className="bg-white border border-neutral-200 rounded-xl p-3 sm:p-4" data-testid="cm-calendar-grid">
          <CalendarMonth pubs={pubs} colorOf={() => color} readOnly month={month} setMonth={setMonth} />
        </div>
      )}

      {sub === "feed" && (
        <div data-testid="cm-feed-grid">
          <FeedGrid pubs={pubs.filter((p) => p.platform === "instagram")} clientColor={color} showDate />
        </div>
      )}

      {sub === "validees" && (
        <div className="space-y-4" data-testid="cm-list-validees">
          {validees.length === 0 && (
            <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">
              Aucune publication validée pour le moment.
            </p>
          )}
          {validees.map((p) => <PubCard key={p.id} p={p} actionable={false} {...cardProps} />)}
        </div>
      )}

      {sub === "reseaux" && (
        <div data-testid="cm-reseaux">
          <SocialNetworksPanel />
        </div>
      )}

      {sub === "rapports" && (
        <div data-testid="cm-rapports-client">
          <CMClientReports />
        </div>
      )}
    </div>
  );
}
